import React, {Component} from 'react';
import {RouteComponentProps, withRouter} from 'react-router-dom';
import api from '../../api';
import Menu from '../Menu';
import PageHeader from '../PageHeader';
import Preloader from '../Preloader';
import './Support.scss';

const SUPPORT_API_ORIGIN = process.env.REACT_APP_SUPPORT_API_URL || '';
const API_URL = `${SUPPORT_API_ORIGIN}/api/staff/support`;
const UPLOAD_URL = `${SUPPORT_API_ORIGIN}/uploadFile`;
const FILE_UPLOAD_TIMEOUT_MS = 30000;
const FILE_UPLOAD_MAX_ATTEMPTS = 2;
const FILE_UPLOAD_RETRY_DELAY_MS = 500;
const DEFAULT_SERVICE_TYPES = [
    'Terminal',
    'Карты',
    'Мобильное приложение мастера',
    'Мобильное приложение клиента',
    'Личный кабинет',
    'Другое',
];

type SupportRouteParams = {
    taskId?: string;
};

export class SupportNew extends Component<RouteComponentProps<SupportRouteParams>, any> {
    private createFilesRef = React.createRef<HTMLInputElement>();
    private messageFilesRef = React.createRef<HTMLInputElement>();
    private messagesRef = React.createRef<HTMLDivElement>();
    private composerRef = React.createRef<HTMLDivElement>();
    private keyboardViewportCleanup?: () => void;

    constructor(props: any) {
        super(props);
        window.scrollTo(0, 0);

        const fallbackAddress = this.getFallbackAddress();
        const fallbackStaffName = this.getFallbackStaffName();

        this.state = {
            isBootLoading: true,
            isTasksLoading: false,
            isDetailLoading: false,
            isSubmitting: false,
            isSendingMessage: false,
            isRatingSubmitting: false,
            error: '',
            notice: '',
            activeView: 'list',
            infoForm: {
                address: [],
                serviceTypes: DEFAULT_SERVICE_TYPES,
                selectedCompanyId: ''
            },
            selectedCompanyId: '',
            activeCompanyAddress: fallbackAddress,
            activeCompanyName: fallbackStaffName,
            tasks: [],
            taskDetail: null,
            selectedTask: null,
            messages: [],
            newMessage: '',
            selectedFiles: [],
            messageFiles: [],
            mediaPreview: null,
            form: {
                serviceType: '',
                problemDescription: '',
                address: fallbackAddress,
                comment: '',
                contactPhone: '',
                terminalPaymentBlocked: false,
                staffName: fallbackStaffName,
            }
        };
    }

    async componentDidMount() {
        this.attachKeyboardViewportTracking();
        await this.bootstrap();
        await this.syncViewWithLocation();
    }

    componentDidUpdate(prevProps: any, prevState: any) {
        if (prevState.messages !== this.state.messages || prevState.activeView !== this.state.activeView) {
            this.scrollMessagesToBottom();
        }
        if (prevProps.location?.pathname !== this.props.location.pathname) {
            void this.syncViewWithLocation();
        }
        if (!prevState.mediaPreview && this.state.mediaPreview) {
            document.addEventListener('keydown', this.handleDocumentKeyDown);
        }
        if (prevState.mediaPreview && !this.state.mediaPreview) {
            document.removeEventListener('keydown', this.handleDocumentKeyDown);
        }
    }

    componentWillUnmount() {
        document.removeEventListener('keydown', this.handleDocumentKeyDown);
        if (this.keyboardViewportCleanup) {
            this.keyboardViewportCleanup();
        }
    }

    attachKeyboardViewportTracking = () => {
        const viewport = typeof window !== 'undefined' ? (window as any).visualViewport : null;
        if (!viewport || typeof document === 'undefined') {
            return;
        }

        const updateOffset = () => {
            const offset = Math.max(0, Math.round(window.innerHeight - viewport.height - viewport.offsetTop));
            document.documentElement.style.setProperty('--support-keyboard-offset', `${offset}px`);
        };

        updateOffset();
        viewport.addEventListener('resize', updateOffset);
        viewport.addEventListener('scroll', updateOffset);

        this.keyboardViewportCleanup = () => {
            viewport.removeEventListener('resize', updateOffset);
            viewport.removeEventListener('scroll', updateOffset);
            document.documentElement.style.removeProperty('--support-keyboard-offset');
        };
    };

    getTimeZone = () => {
        try {
            return Intl.DateTimeFormat().resolvedOptions().timeZone || 'Europe/Moscow';
        } catch {
            return 'Europe/Moscow';
        }
    };

    getStoredActiveCompany = () => {
        try {
            const raw = sessionStorage.getItem('companyActive');
            return raw ? JSON.parse(raw) : null;
        } catch {
            return null;
        }
    };

    getFallbackAddress = () => {
        const company = this.getStoredActiveCompany();
        if (!company) {
            return '';
        }
        return [company.city, company.address].filter(Boolean).join(', ');
    };

    getFallbackStaffName = () => {
        const company = this.getStoredActiveCompany();
        return company?.nameClient || '';
    };

    handleDocumentKeyDown = (event: KeyboardEvent) => {
        if (event.key === 'Escape') {
            this.closeMediaPreview();
        }
    };

    usesCompanySelect = () => (this.state.infoForm.address || []).length > 0;

    getSelectedCompanyOption = () => {
        const addresses = this.state.infoForm.address || [];
        const selectedCompanyId = String(this.state.selectedCompanyId || this.state.form.address || '');
        return addresses.find((item: any) => String(item.id) === selectedCompanyId) || null;
    };

    getSelectedCompanyAddress = () => {
        const company = this.getSelectedCompanyOption();
        if (company?.address) {
            return company.address;
        }
        return this.state.activeCompanyAddress || '';
    };

    parseTaskDate = (value?: string) => {
        if (!value) {
            return 0;
        }

        const match = value.match(/^(\d{2})\.(\d{2})\.(\d{4})(?:\s+(\d{2}):(\d{2}))?$/);
        if (!match) {
            return 0;
        }

        const [, day, month, year, hours = '00', minutes = '00'] = match;
        return new Date(
            Number(year),
            Number(month) - 1,
            Number(day),
            Number(hours),
            Number(minutes),
            0,
            0
        ).getTime();
    };

    sortTasksByActivity = (tasks: any[]) => {
        return [...tasks].sort((left: any, right: any) => {
            const rightValue = this.parseTaskDate(right?.dateLastMess || right?.dateCreate);
            const leftValue = this.parseTaskDate(left?.dateLastMess || left?.dateCreate);
            if (rightValue !== leftValue) {
                return rightValue - leftValue;
            }
            return Number(right?.id || 0) - Number(left?.id || 0);
        });
    };

    buildParams = (companyId = '') => {
        const params: any = {timeZone: this.getTimeZone()};
        if (this.usesCompanySelect() && companyId) {
            params.companyId = companyId;
        }
        return params;
    };

    handleApiError = (error: any, fallbackMessage: string) => {
        if (error?.response?.status === 401) {
            window.location.href = '/start';
            return fallbackMessage;
        }
        return error?.response?.data?.error || fallbackMessage;
    };

    getRouteState = () => {
        const pathname = (this.props.location.pathname || '/support').replace(/\/+$/, '') || '/support';

        if (pathname === '/support') {
            return {view: 'list'};
        }

        if (pathname === '/support/new') {
            return {view: 'create'};
        }

        const ticketMatch = pathname.match(/^\/support\/(\d+)$/);
        if (ticketMatch) {
            return {view: 'detail', taskId: ticketMatch[1]};
        }

        return {view: 'invalid'};
    };

    navigateToPath = (pathname: string, replace = false) => {
        if (this.props.location.pathname === pathname) {
            return;
        }

        if (replace) {
            this.props.history.replace(pathname);
            return;
        }

        this.props.history.push(pathname);
    };

    resetCreateFilesInput = () => {
        if (this.createFilesRef.current) {
            this.createFilesRef.current.value = '';
        }
    };

    resetMessageFilesInput = () => {
        if (this.messageFilesRef.current) {
            this.messageFilesRef.current.value = '';
        }
    };

    getCreateFormState = () => {
        const address = this.usesCompanySelect() ? (this.state.selectedCompanyId || this.state.form.address) : (this.state.form.address || this.state.activeCompanyAddress);

        return {
            ...this.state.form,
            serviceType: '',
            problemDescription: '',
            address,
            comment: '',
            contactPhone: '',
            terminalPaymentBlocked: false,
            staffName: this.state.form.staffName || this.state.activeCompanyName,
        };
    };

    enterCreateView = () => {
        if (this.state.activeView === 'create') {
            return;
        }

        window.scrollTo(0, 0);
        this.resetCreateFilesInput();
        this.resetMessageFilesInput();
        this.setState({
            activeView: 'create',
            error: '',
            notice: '',
            taskDetail: null,
            selectedTask: null,
            messages: [],
            newMessage: '',
            messageFiles: [],
            selectedFiles: [],
            mediaPreview: null,
            form: this.getCreateFormState(),
        });
    };

    enterListView = () => {
        if (this.state.activeView === 'list' && !this.state.taskDetail && !this.state.selectedTask && this.state.messages.length === 0 && this.state.messageFiles.length === 0) {
            return;
        }

        window.scrollTo(0, 0);
        this.resetCreateFilesInput();
        this.resetMessageFilesInput();
        this.setState({
            activeView: 'list',
            error: '',
            taskDetail: null,
            selectedTask: null,
            messages: [],
            newMessage: '',
            messageFiles: [],
            selectedFiles: [],
            mediaPreview: null,
        });
    };

    getCompanyCandidates = () => {
        const addresses = this.state.infoForm.address || [];
        const candidateIds = [String(this.state.selectedCompanyId || '')]
            .concat(addresses.map((item: any) => String(item.id || '')))
            .filter((item: string, index: number, list: string[]) => Boolean(item) ? list.indexOf(item) === index : false);

        if (!candidateIds.length) {
            return [''];
        }

        return candidateIds;
    };

    loadTaskDetail = async (taskId: number | string, companyId = '', fallbackTask: any = null) => {
        window.scrollTo(0, 0);
        this.resetMessageFilesInput();
        this.setState({
            activeView: 'detail',
            isDetailLoading: true,
            error: '',
            selectedTask: fallbackTask,
            taskDetail: null,
            messages: [],
            newMessage: '',
            messageFiles: [],
            mediaPreview: null,
        });

        try {
            const [taskResponse, messages] = await Promise.all([
                api.get(API_URL + '/tasks/' + taskId, {params: this.buildParams(companyId), withCredentials: true}),
                this.loadTaskMessages(taskId, companyId)
            ]);

            const taskDetail = taskResponse.data || fallbackTask;
            this.setState({
                isDetailLoading: false,
                selectedTask: fallbackTask || taskDetail,
                taskDetail,
                messages,
            });
            return taskDetail;
        } catch (error) {
            this.setState({
                activeView: 'list',
                isDetailLoading: false,
                selectedTask: null,
                taskDetail: null,
                messages: [],
                newMessage: '',
                messageFiles: [],
                mediaPreview: null,
                error: this.handleApiError(error, 'Не удалось открыть тикет')
            });
            this.navigateToPath('/support', true);
            return null;
        }
    };

    resolveTaskById = async (taskId: string) => {
        const companyIds = this.getCompanyCandidates();

        for (const companyId of companyIds) {
            try {
                const response = await api.get(API_URL + '/tasks/' + taskId, {
                    params: this.buildParams(companyId),
                    withCredentials: true,
                });

                return {
                    task: response.data || {id: taskId, companyId},
                    companyId,
                };
            } catch (error) {
                if (error?.response?.status === 401) {
                    this.handleApiError(error, 'Не удалось открыть тикет');
                    return null;
                }
            }
        }

        return null;
    };

    openTaskById = async (taskId: string) => {
        const currentTask = this.state.taskDetail || this.state.selectedTask;
        if (this.state.activeView === 'detail' && !this.state.isDetailLoading && String(currentTask?.id || '') === String(taskId)) {
            return;
        }

        const taskFromList = (this.state.tasks || []).find((item: any) => String(item.id) === String(taskId));
        if (taskFromList) {
            const companyId = taskFromList?.companyId ? String(taskFromList.companyId) : this.state.selectedCompanyId;
            await this.loadTaskDetail(taskId, companyId, taskFromList);
            return;
        }

        const resolvedTask = await this.resolveTaskById(taskId);
        if (!resolvedTask) {
            this.setState({
                activeView: 'list',
                error: 'Тикет не найден или недоступен',
                taskDetail: null,
                selectedTask: null,
                messages: [],
                newMessage: '',
                messageFiles: [],
                mediaPreview: null,
            });
            this.navigateToPath('/support', true);
            return;
        }

        if (resolvedTask.companyId && resolvedTask.companyId !== this.state.selectedCompanyId) {
            this.setState({
                selectedCompanyId: resolvedTask.companyId,
                form: {
                    ...this.state.form,
                    address: resolvedTask.companyId,
                }
            });
            void this.loadTasks(resolvedTask.companyId);
        }

        await this.loadTaskDetail(taskId, resolvedTask.companyId, resolvedTask.task);
    };

    syncViewWithLocation = async () => {
        if (this.state.isBootLoading) {
            return;
        }

        const routeState = this.getRouteState();
        if (routeState.view === 'create') {
            this.enterCreateView();
            return;
        }

        if (routeState.view === 'detail' && routeState.taskId) {
            await this.openTaskById(routeState.taskId);
            return;
        }

        if (routeState.view === 'invalid') {
            this.navigateToPath('/support', true);
            return;
        }

        this.enterListView();
    };

    bootstrap = async () => {
        const fallbackAddress = this.getFallbackAddress();
        const fallbackStaffName = this.getFallbackStaffName();

        try {
            const response = await api.get(API_URL + '/task/info', {withCredentials: true});
            const info = response.data || {};
            const addresses = Array.isArray(info.address) ? info.address : [];
            const selectedCompanyId = info.selectedCompanyId ? String(info.selectedCompanyId) : (addresses[0]?.id ? String(addresses[0].id) : '');

            this.setState({
                isBootLoading: false,
                infoForm: {
                    address: addresses,
                    serviceTypes: Array.isArray(info.serviceTypes) && info.serviceTypes.length > 0 ? info.serviceTypes : DEFAULT_SERVICE_TYPES,
                    selectedCompanyId
                },
                selectedCompanyId,
                activeCompanyAddress: fallbackAddress,
                activeCompanyName: fallbackStaffName,
                form: {
                    serviceType: '',
                    problemDescription: '',
                    address: addresses.length > 0 ? selectedCompanyId : fallbackAddress,
                    comment: '',
                    contactPhone: '',
                    terminalPaymentBlocked: false,
                    staffName: fallbackStaffName,
                }
            });

            await this.loadTasks(addresses.length > 0 ? selectedCompanyId : '');
        } catch (error) {
            this.setState({
                isBootLoading: false,
                error: this.handleApiError(error, 'Не удалось загрузить поддержку'),
                activeCompanyAddress: fallbackAddress,
                activeCompanyName: fallbackStaffName,
                form: {
                    ...this.state.form,
                    address: fallbackAddress,
                    contactPhone: '',
                    terminalPaymentBlocked: false,
                    staffName: fallbackStaffName,
                }
            });

            await this.loadTasks('');
        }
    };

    loadTasks = async (companyId = '') => {
        this.setState({isTasksLoading: true, error: ''});
        try {
            const response = await api.get(API_URL + '/tasks', {
                params: this.buildParams(companyId),
                withCredentials: true,
            });
            const tasks = Array.isArray(response.data) ? response.data : [];
            this.setState({tasks: this.sortTasksByActivity(tasks), isTasksLoading: false});
        } catch (error) {
            this.setState({
                isTasksLoading: false,
                error: this.handleApiError(error, 'Не удалось загрузить тикеты')
            });
        }
    };

    loadTaskMessages = async (taskId: number | string, companyId = '') => {
        const response = await api.get(API_URL + '/task/messeage/' + taskId, {
            params: this.buildParams(companyId),
            withCredentials: true,
        });
        return Array.isArray(response.data) ? response.data : [];
    };

    openTask = (task: any) => this.navigateToPath('/support/' + task.id);

    openCreateForm = () => this.navigateToPath('/support/new');

    closeCurrentView = () => this.navigateToPath('/support');

    handleCompanyChange = async (event: any) => {
        const companyId = event.target.value;
        this.navigateToPath('/support', true);
        this.setState({
            selectedCompanyId: companyId,
            activeView: 'list',
            taskDetail: null,
            selectedTask: null,
            messages: [],
            form: {...this.state.form, address: companyId}
        });
        await this.loadTasks(companyId);
    };

    handleFormChange = (event: any) => {
        const {name, type, checked, value} = event.target;
        this.setState({form: {...this.state.form, [name]: type === 'checkbox' ? checked : value}});
    };
    handleMessageTextChange = (event: any) => this.setState({newMessage: event.target.value});
    handleCreateFilesChange = (event: any) => this.setState({selectedFiles: Array.from(event.target.files || [])});
    handleMessageFilesChange = (event: any) => this.setState({messageFiles: Array.from(event.target.files || [])});
    clearCreateFiles = () => {
        this.resetCreateFilesInput();
        this.setState({selectedFiles: []});
    };
    clearMessageFiles = () => {
        this.resetMessageFilesInput();
        this.setState({messageFiles: []});
    };

    uploadFileOnce = async (file: File) => {
        const controller = typeof AbortController !== 'undefined' ? new AbortController() : null;
        let timeoutId = 0;

        const uploadPromise = (async () => {
            const formData = new FormData();
            formData.append('file', file);
            const response = await fetch(UPLOAD_URL, {
                method: 'POST',
                body: formData,
                ...(controller ? {signal: controller.signal} : {}),
            });
            if (!response.ok) {
                throw new Error(`upload_failed_${response.status}`);
            }
            const result = await response.json();
            if (!result?.url) {
                throw new Error('upload_url_missing');
            }
            return result.url;
        })();

        const timeoutPromise = new Promise<string>((_, reject) => {
            timeoutId = window.setTimeout(() => {
                if (controller) {
                    controller.abort();
                }
                reject(new Error('upload_timeout'));
            }, FILE_UPLOAD_TIMEOUT_MS);
        });

        try {
            return await Promise.race([uploadPromise, timeoutPromise]);
        } finally {
            window.clearTimeout(timeoutId);
        }
    };

    waitBeforeUploadRetry = () => new Promise((resolve) => window.setTimeout(resolve, FILE_UPLOAD_RETRY_DELAY_MS));

    uploadFileWithRetry = async (file: File) => {
        let lastError: any = null;
        for (let attempt = 1; attempt <= FILE_UPLOAD_MAX_ATTEMPTS; attempt += 1) {
            try {
                return await this.uploadFileOnce(file);
            } catch (error) {
                lastError = error;
                if (attempt < FILE_UPLOAD_MAX_ATTEMPTS) {
                    await this.waitBeforeUploadRetry();
                }
            }
        }
        throw lastError || new Error('upload_failed');
    };

    uploadFiles = async (files: File[]) => {
        const urls: string[] = [];
        for (const file of files) {
            urls.push(await this.uploadFileWithRetry(file));
        }
        return urls;
    };

    uploadTaskFiles = async (files: File[]) => {
        const urls: string[] = [];
        const failedFiles: File[] = [];

        for (const file of files) {
            try {
                urls.push(await this.uploadFileWithRetry(file));
            } catch (error) {
                failedFiles.push(file);
            }
        }

        return {urls, failedFiles};
    };

    attachFilesToTask = async (taskId: number | string, files: string[], companyId: string, staffName: string) => {
        if (!files.length) {
            return;
        }

        await api.post(API_URL + '/tasks/messeage/' + taskId, {
            message: '',
            files,
            staffName,
        }, {
            params: this.buildParams(companyId),
            withCredentials: true,
        });
    };

    submitTask = async (event: any) => {
        event.preventDefault();
        const {form, selectedFiles} = this.state;
        if (!form.serviceType || !form.problemDescription || !form.address) {
            this.setState({error: 'Заполните сервис, описание проблемы и адрес салона'});
            return;
        }

        this.setState({isSubmitting: true, error: '', notice: ''});
        let taskCreated = false;
        try {
            const companyId = this.usesCompanySelect() ? String(form.address) : '';
            const staffName = form.staffName || this.state.activeCompanyName;
            const taskResponse = await api.post(API_URL + '/task/setTask', {
                serviceType: form.serviceType,
                problemDescription: form.problemDescription,
                address: form.address,
                companyId,
                comment: form.comment,
                contactPhone: form.contactPhone,
                terminalPaymentBlocked: Boolean(form.terminalPaymentBlocked),
                staffName,
            }, {withCredentials: true});
            taskCreated = true;

            const taskId = taskResponse?.data?.id;
            let failedAttachmentCount = 0;
            if (selectedFiles.length > 0) {
                if (!taskId) {
                    failedAttachmentCount = selectedFiles.length;
                } else {
                    const {urls, failedFiles} = await this.uploadTaskFiles(selectedFiles);
                    failedAttachmentCount = failedFiles.length;
                    if (urls.length > 0) {
                        try {
                            await this.attachFilesToTask(taskId, urls, companyId, staffName);
                        } catch (error) {
                            failedAttachmentCount += urls.length;
                        }
                    }
                }
            }

            const notice = failedAttachmentCount > 0
                ? `Тикет${taskId ? ` #${taskId}` : ''} создан, но не удалось прикрепить ${failedAttachmentCount} из ${selectedFiles.length} файлов. Добавьте их позже в переписке.`
                : '';

            if (this.createFilesRef.current) {
                this.createFilesRef.current.value = '';
            }

            await this.loadTasks(companyId);
            this.resetCreateFilesInput();
            this.setState({
                notice,
                selectedFiles: [],
                form: {
                    ...this.state.form,
                    serviceType: '',
                    problemDescription: '',
                    address: this.usesCompanySelect() ? String(form.address) : this.state.activeCompanyAddress,
                    comment: '',
                    contactPhone: '',
                    terminalPaymentBlocked: false,
                }
            });
            this.navigateToPath('/support');
        } catch (error) {
            this.setState({
                error: taskCreated
                    ? 'Тикет создан, но не удалось завершить обработку вложений. Добавьте их позже в переписке.'
                    : this.handleApiError(error, 'Не удалось создать тикет')
            });
        } finally {
            this.setState({isSubmitting: false});
        }
    };

    sendMessage = async () => {
        const task = this.state.taskDetail || this.state.selectedTask;
        if (!task) {
            return;
        }
        if (!this.state.newMessage.trim() && this.state.messageFiles.length === 0) {
            this.setState({error: 'Введите сообщение или приложите файл'});
            return;
        }

        const companyId = task.companyId ? String(task.companyId) : this.state.selectedCompanyId;
        this.setState({isSendingMessage: true, error: ''});

        try {
            const files = await this.uploadFiles(this.state.messageFiles);
            await api.post(API_URL + '/tasks/messeage/' + task.id, {
                message: this.state.newMessage,
                files,
                staffName: this.state.form.staffName || this.state.activeCompanyName,
            }, {
                params: this.buildParams(companyId),
                withCredentials: true,
            });

            const messages = await this.loadTaskMessages(task.id, companyId);
            if (this.messageFilesRef.current) {
                this.messageFilesRef.current.value = '';
            }
            this.setState({isSendingMessage: false, newMessage: '', messageFiles: [], messages});
        } catch (error) {
            this.setState({isSendingMessage: false, error: this.handleApiError(error, 'Не удалось отправить сообщение')});
        }
    };

    submitTaskRating = async (score: number) => {
        const task = this.state.taskDetail || this.state.selectedTask;
        if (!task || this.state.isRatingSubmitting) {
            return;
        }

        const companyId = task.companyId ? String(task.companyId) : this.state.selectedCompanyId;
        this.setState({isRatingSubmitting: true, error: ''});

        try {
            const response = await api.post(API_URL + '/tasks/' + task.id + '/rating', {
                score,
                staffName: this.state.form.staffName || this.state.activeCompanyName,
            }, {
                params: this.buildParams(companyId),
                withCredentials: true,
            });
            const rating = response.data?.rating;
            if (rating) {
                const patchRating = (item: any) => item && String(item.id) === String(task.id) ? {...item, rating} : item;
                this.setState({
                    isRatingSubmitting: false,
                    taskDetail: patchRating(this.state.taskDetail),
                    selectedTask: patchRating(this.state.selectedTask),
                    tasks: (this.state.tasks || []).map(patchRating),
                });
            } else {
                this.setState({isRatingSubmitting: false});
            }
        } catch (error) {
            this.setState({
                isRatingSubmitting: false,
                error: this.handleApiError(error, 'Не удалось сохранить оценку')
            });
        }
    };

    scrollMessagesToBottom = () => {
        if (this.messagesRef.current) {
            this.messagesRef.current.scrollTop = this.messagesRef.current.scrollHeight;
        }
    };

    keepComposerVisible = () => {
        window.setTimeout(() => {
            this.scrollMessagesToBottom();
            this.composerRef.current?.scrollIntoView({behavior: 'smooth', block: 'end'});
        }, 120);
    };

    getTaskBody = (task: any) => task && typeof task.body === 'object' && !Array.isArray(task.body) ? task.body : {};
    getTaskProblemText = (task: any) => this.getTaskBody(task).problemDescription || this.getTaskBody(task).comment || '';
    getFileUrl = (file: any) => typeof file === 'string' ? file : (file?.link || file?.url || '');
    isImageUrl = (url: string) => /\.(jpg|jpeg|png|gif|webp|bmp|svg)(\?.*)?$/i.test(url);
    isVideoUrl = (url: string) => /\.(mp4|webm|mov|m4v|avi)(\?.*)?$/i.test(url);
    formatStatus = (status: string) => status === 'Свободно' ? 'В очереди' : (status || 'Без статуса');
    formatFileSize = (size?: number) => {
        if (!size) {
            return '';
        }

        if (size < 1024) {
            return size + ' Б';
        }

        if (size < 1024 * 1024) {
            return (size / 1024).toFixed(1).replace('.0', '') + ' КБ';
        }

        return (size / (1024 * 1024)).toFixed(1).replace('.0', '') + ' МБ';
    };

    renderSelectedFiles(files: any[]) {
        if (!files.length) {
            return null;
        }
        return <div className='support-file-list'>{files.map((file: any, index: number) => <div key={index} className='support-file-chip'><span>{file.name || 'Файл'}</span>{file?.size ? <span className='support-file-chip-meta'>{this.formatFileSize(file.size)}</span> : null}</div>)}</div>;
    }

    renderMessageFiles(files: any[]) {
        if (!files.length) {
            return null;
        }
        return <div className='support-message-files'>{files.map((file: any, index: number) => {
            const fileUrl = this.getFileUrl(file);
            if (!fileUrl) {
                return null;
            }
            if (this.isImageUrl(fileUrl)) {
                return <button key={index} type='button' className='support-message-media-button support-message-image' onClick={() => this.openMediaPreview(fileUrl, 'image')}><img src={fileUrl} alt='Вложение'/></button>;
            }
            if (this.isVideoUrl(fileUrl)) {
                return <button key={index} type='button' className='support-message-media-button support-message-video' onClick={() => this.openMediaPreview(fileUrl, 'video')}><video src={fileUrl} muted playsInline preload='metadata'/><span className='support-message-video-badge'>Видео</span></button>;
            }
            return <a key={index} className='support-message-link' href={fileUrl} target='_blank' rel='noopener noreferrer'>{fileUrl}</a>;
        })}</div>;
    }

    openMediaPreview = (url: string, type: 'image' | 'video') => {
        this.setState({mediaPreview: {url, type}});
    };

    closeMediaPreview = () => {
        this.setState({mediaPreview: null});
    };

    renderFilePicker({files, inputId, inputRef, onChange, onClear, hint, variant}: any) {
        const hasFiles = files.length > 0;
        const isLightCentered = variant === 'light-centered';
        const pickerClassName = 'support-file-picker' + (isLightCentered ? ' support-file-picker-light' : '');
        const triggerClassName = 'support-file-trigger' + (hasFiles ? ' support-file-trigger-active' : '') + (isLightCentered ? ' support-file-trigger-light' : '');
        const clearClassName = 'support-file-clear' + (isLightCentered ? ' support-file-clear-centered' : '');

        return (
            <div className={pickerClassName}>
                <input id={inputId} ref={inputRef} type='file' multiple className='support-file-input-native' onChange={onChange}/>
                <label htmlFor={inputId} className={triggerClassName}>
                    <span className='support-file-trigger-icon' aria-hidden='true'>
                        <svg viewBox='0 0 24 24'>
                            <path d='M8.5 12.5 13 8a3 3 0 1 1 4.2 4.2l-6.1 6.1a4.5 4.5 0 0 1-6.4-6.4l6.5-6.4'/>
                        </svg>
                    </span>
                    <span className='support-file-trigger-copy'>
                        <span className='support-file-trigger-title'>{hasFiles ? 'Изменить вложения' : 'Выбрать файлы'}</span>
                        <span className='support-file-trigger-text'>{hasFiles ? 'Выбрано файлов: ' + files.length : hint}</span>
                    </span>
                </label>
                {hasFiles ? <button className={clearClassName} type='button' onClick={onClear}>Очистить</button> : null}
                {this.renderSelectedFiles(files)}
            </div>
        );
    }

    renderRatingBlock(task: any) {
        const rating = task?.rating;
        if (!rating?.requested) {
            return null;
        }

        const score = Number(rating.score || 0);

        return (
            <div className='support-rating-card mt-3'>
                <div className='support-rating-head'>
                    <div>
                        <div className='support-section-label'>Оценка закрытия</div>
                        <div className='support-rating-value'>{score > 0 ? `${score}/5` : 'Ожидает оценки'}</div>
                    </div>
                    {!rating.canRate ? <span className='support-rating-note'>{score > 0 ? 'Сохранена' : 'Не оценено'}</span> : null}
                </div>
                {rating.canRate ? (
                    <div className='support-rating-buttons'>
                        {[1, 2, 3, 4, 5].map((value) => (
                            <button
                                key={value}
                                type='button'
                                className={score === value ? 'active' : ''}
                                disabled={this.state.isRatingSubmitting}
                                onClick={() => this.submitTaskRating(value)}
                            >
                                {value}
                            </button>
                        ))}
                    </div>
                ) : null}
            </div>
        );
    }

    render() {
        if (this.state.isBootLoading) {
            return <Preloader/>;
        }

        const task = this.state.taskDetail || this.state.selectedTask;
        const body = this.getTaskBody(task);
        const addresses = this.state.infoForm.address || [];
        const mediaPreview = this.state.mediaPreview;

        return (
            <div className='main support-page'>
                <div className='content mx-w-content'>
                    <PageHeader pageTitle='Поддержка' backBtnUrl='/'/>
                    <div className='pt-header'></div>

                    {this.state.notice ? <div className='support-alert ml-3 mr-3 mb-3'>{this.state.notice}</div> : null}
                    {this.state.error ? <div className='support-alert ml-3 mr-3 mb-3'>{this.state.error}</div> : null}

                    {this.state.activeView === 'create' ? (
                        <div className='support-form-wrap'>
                            <div className='ml-3 mr-3 mb-3'><button className='support-back-button' type='button' onClick={this.closeCurrentView}>Назад к списку</button></div>
                            <form className='chio-card ml-3 mr-3 mb-3 p-3 support-form' onSubmit={this.submitTask}>
                                <div className='support-form-title'>Новый тикет</div>
                                <div className='support-field'><label>Сервис с проблемой</label><select name='serviceType' className='support-select' value={this.state.form.serviceType} onChange={this.handleFormChange} required><option value=''>Выберите сервис</option>{(this.state.infoForm.serviceTypes || DEFAULT_SERVICE_TYPES).map((item: string) => <option key={item} value={item}>{item}</option>)}</select></div>
                                {addresses.length > 0 ? (
                                    <div className='support-field'><label>Адрес салона</label><select name='address' className='support-select' value={this.state.form.address} onChange={this.handleFormChange} required><option value=''>Выберите салон</option>{addresses.map((item: any) => <option key={item.id} value={item.id}>{item.address}</option>)}</select><div className='support-form-hint'>Адреса подгружаются по салонам, где привязан мастер.</div></div>
                                ) : (
                                    <div className='support-field'><label>Адрес салона</label><input name='address' className='support-select' value={this.state.form.address} onChange={this.handleFormChange} placeholder='Например: Москва, ул. Примерная, 10' required/><div className='support-form-hint'>Если привязка салона не найдена, адрес можно ввести вручную.</div></div>
                                )}
                                <div className='support-field'><label>Описание проблемы</label><textarea name='problemDescription' className='support-textarea' value={this.state.form.problemDescription} onChange={this.handleFormChange} placeholder='Что именно не работает и как это воспроизвести?' required/></div>
                                <div className='support-field'><label>Телефон для связи</label><input type='tel' inputMode='tel' name='contactPhone' className='support-select' value={this.state.form.contactPhone || ''} onChange={this.handleFormChange} placeholder='+7 999 123-45-67'/><div className='support-form-hint'>По закрытию тикета или если возникнут вопросы, мы вам позвоним.</div></div>
                                <label className='support-urgent-checkbox'>
                                    <input type='checkbox' name='terminalPaymentBlocked' checked={Boolean(this.state.form.terminalPaymentBlocked)} onChange={this.handleFormChange}/>
                                    <span className='support-urgent-checkbox-text'>
                                        <span>Терминал не принимает оплаты</span>
                                        <span className='support-priority-note'>(Эта задача будет взята в приоритет)</span>
                                    </span>
                                </label>
                                <div className='support-field'><label>Комментарий</label><textarea name='comment' className='support-textarea support-textarea-small' value={this.state.form.comment} onChange={this.handleFormChange} placeholder='Дополнительные детали, если нужно'/></div>
                                <div className='support-field'><label>Файлы</label>{this.renderFilePicker({files: this.state.selectedFiles, inputId: 'support-create-files', inputRef: this.createFilesRef, onChange: this.handleCreateFilesChange, onClear: this.clearCreateFiles, hint: 'Скриншоты, фото, PDF и другие файлы'})}</div>
                                <button className='support-primary-button' type='submit' disabled={this.state.isSubmitting}>{this.state.isSubmitting ? 'Отправляем...' : 'Создать тикет'}</button>
                            </form>
                        </div>
                    ) : this.state.activeView === 'detail' ? (
                        <div className='support-detail'>
                            <div className='ml-3 mr-3 mb-3'><button className='support-back-button' type='button' onClick={this.closeCurrentView}>Назад к списку</button></div>
                            {this.state.isDetailLoading ? <div className='chio-card ml-3 mr-3 p-3 mb-3'>Загружаем переписку...</div> : (
                                <>
                                    <div className='chio-card ml-3 mr-3 mb-3 p-3 support-detail-card'>
                                        <div className='support-task-top'><div className='support-task-number'>Тикет #{task?.id}</div><div className='support-task-status'>{this.formatStatus(task?.status)}</div></div>
                                        {task?.terminalPaymentBlocked || body.terminalPaymentBlocked ? <div className='support-urgent-badge'>Терминал не принимает оплаты</div> : null}
                                        <div className='support-task-service mb-3'>{body.serviceType || task?.title || 'Без названия'}</div>
                                        {this.renderRatingBlock(task)}
                                        <div className='support-detail-grid'>
                                            <div><div className='support-section-label'>Салон</div><div className='txt-middle'>{body.address || task?.companyAddress || 'Не указан'}</div></div>
                                            <div><div className='support-section-label'>Телефон для связи</div><div className='txt-middle'>{body.contactPhone || 'Не указан'}</div></div>
                                            <div><div className='support-section-label'>Создан</div><div className='txt-middle'>{task?.dateCreate || 'Не указано'}</div></div>
                                            <div><div className='support-section-label'>Последняя активность</div><div className='txt-middle'>{task?.dateLastMess || 'Не указано'}</div></div>
                                        </div>
                                        <div className='support-problem-card mt-3'>
                                            <div className='support-section-label'>Описание проблемы</div>
                                            <div className='support-problem-text'>{body.problemDescription || 'Не указано'}</div>
                                            {body.comment ? <><div className='support-section-label mt-3'>Комментарий</div><div className='support-problem-text'>{body.comment}</div></> : null}
                                        </div>
                                    </div>

                                    <div className='chio-card ml-3 mr-3 mb-3 p-3 support-chat-card'>
                                        <div className='support-form-title'>Переписка</div>
                                        <div className='support-chat-messages' ref={this.messagesRef}>
                                            {this.state.messages.length === 0 ? <div className='support-empty-chat'>Пока нет сообщений. Можно написать первым.</div> : this.state.messages.map((message: any, index: number) => (
                                                <div key={index} className={'support-message ' + (message.author === 'mine' ? 'support-message-mine' : 'support-message-other')}>
                                                    <div className='support-message-head'><span>{message.author === 'mine' ? 'Я' : (message.author || 'Поддержка')}</span><span>{message.createdAt || ''}</span></div>
                                                    <div className='support-message-text' dangerouslySetInnerHTML={{__html: message.text || ''}}/>
                                                    {this.renderMessageFiles(Array.isArray(message.files) ? message.files : [])}
                                                </div>
                                            ))}
                                        </div>
                                        <div className='support-composer' ref={this.composerRef}>
                                            <textarea className='support-textarea support-composer-textarea' value={this.state.newMessage} onChange={this.handleMessageTextChange} onFocus={this.keepComposerVisible} placeholder='Введите сообщение для поддержки'/>
                                            {this.renderFilePicker({files: this.state.messageFiles, inputId: 'support-message-files', inputRef: this.messageFilesRef, onChange: this.handleMessageFilesChange, onClear: this.clearMessageFiles, hint: 'Добавьте скриншоты или документы к сообщению', variant: 'light-centered'})}
                                            <button className='support-primary-button' type='button' onClick={this.sendMessage} disabled={this.state.isSendingMessage}>{this.state.isSendingMessage ? 'Отправляем...' : 'Отправить'}</button>
                                        </div>
                                    </div>
                                </>
                            )}
                        </div>
                    ) : (
                        <>
                            <div className='chio-card ml-3 mr-3 mb-3 p-3 support-hero-card'>
                                <div className='support-hero-title'>Поддержка мастеров</div>
                                <div className='txt-middle support-hero-text'>Если что-то не работает в приложении, терминале, картах или личном кабинете, создайте тикет и приложите скриншоты.</div>
                                {this.usesCompanySelect() && this.getSelectedCompanyAddress() ? <div className='support-meta-row mt-3'><span className='support-meta-pill support-meta-pill-light'>{this.getSelectedCompanyAddress()}</span><span className='support-meta-pill'>{addresses.length} адресов в доступе</span></div> : null}
                                {!this.usesCompanySelect() && this.state.activeCompanyAddress ? <div className='txt-middle mt-2 support-hero-text'>Текущий салон: {this.state.activeCompanyAddress}</div> : null}
                                <div className='support-actions mt-3'><button className='support-primary-button' type='button' onClick={this.openCreateForm}>Создать тикет</button></div>
                            </div>

                            {addresses.length > 0 ? <div className='chio-card ml-3 mr-3 mb-3 p-3 support-filter-card'><div className='support-filter-head'><div><div className='support-section-label'>Салон</div><div className='support-filter-title'>Фильтр по месту работы</div></div><div className='support-meta-pill'>{this.state.tasks.length} тикетов</div></div><select className='support-select mt-3' value={this.state.selectedCompanyId} onChange={this.handleCompanyChange}>{addresses.map((item: any) => <option key={item.id} value={item.id}>{item.address}</option>)}</select></div> : null}

                            {addresses.length > 0 ? <div className='ml-3 mr-3 mb-3 support-form-hint'>В списке показываются только тикеты по выбранному салону.</div> : null}

                            {this.state.isTasksLoading ? <div className='chio-card ml-3 mr-3 p-3 mb-3'>Загружаем тикеты...</div> : this.state.tasks.length === 0 ? (
                                <div className='chio-card ml-3 mr-3 p-4 mb-3 support-empty-state'>
                                    <div className='txt-large mb-2'>Пока нет тикетов</div>
                                    <div className='txt-middle mb-3'>Создайте обращение, и поддержка ответит прямо внутри этого экрана.</div>
                                    <button className='support-secondary-button' type='button' onClick={this.openCreateForm}>Открыть форму</button>
                                </div>
                            ) : <div className='support-list'>{this.state.tasks.map((item: any) => {
                                const itemBody = this.getTaskBody(item);
                                return <button key={item.id} type='button' className='chio-card ml-3 mr-3 mb-3 p-3 support-task-card' onClick={() => this.openTask(item)}>
                                    <div className='support-task-top'><div className='support-task-number'>Тикет #{item.id}</div><div className='support-task-status'>{this.formatStatus(item.status)}</div></div>
                                    {item.terminalPaymentBlocked || itemBody.terminalPaymentBlocked ? <div className='support-urgent-badge'>Терминал не принимает оплаты</div> : null}
                                    <div className='support-task-service'>{itemBody.serviceType || item.title || 'Без названия'}</div>
                                    <div className='txt-middle mb-2 support-task-text'>{this.getTaskProblemText(item) || 'Описание не указано'}</div>
                                    <div className='support-task-meta support-meta-row'>
                                        <span className='support-meta-pill support-meta-pill-light'>{itemBody.address || item.companyAddress || 'Адрес не указан'}</span>
                                        <span className='support-meta-pill'>Создан: {item.dateCreate || 'неизвестно'}</span>
                                        <span className='support-meta-pill'>Активность: {item.dateLastMess || 'нет'}</span>
                                    </div>
                                    <div className='support-task-action'><span>Открыть переписку</span><span className='support-task-action-arrow' aria-hidden='true'>›</span></div>
                                </button>;
                            })}</div>}
                        </>
                    )}
                </div>
                {mediaPreview ? (
                    <div className='support-media-lightbox' onClick={this.closeMediaPreview} role='presentation'>
                        <div className='support-media-lightbox-dialog' onClick={(event: any) => event.stopPropagation()} role='dialog' aria-modal='true' aria-label='Предпросмотр вложения'>
                            <button type='button' className='support-media-lightbox-close' onClick={this.closeMediaPreview} aria-label='Закрыть медиа'>
                                <span className='support-media-lightbox-close-icon' aria-hidden='true'>×</span>
                            </button>
                            <div className='support-media-lightbox-content'>
                                {mediaPreview.type === 'image' ? <img className='support-media-lightbox-image' src={mediaPreview.url} alt='Вложение'/> : <video className='support-media-lightbox-video' src={mediaPreview.url} controls playsInline autoPlay/>}
                            </div>
                        </div>
                    </div>
                ) : null}
                <Menu></Menu>
            </div>
        );
    }
}

export default withRouter(SupportNew);
